import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Truck, Megaphone, BadgePercent, Store, Phone, Mail } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import GrowthChart from "@/components/GrowthChart";
import { submitInquiry } from "@/lib/api";
import { CONTACT } from "@/data/site";

const BENEFITS = [
  {
    icon: Truck,
    title: "Reliable Supply Chain",
    body: "Scheduled dispatches and buffer stock planning keep your shelves full through peak season.",
    testid: "benefit-tile-supply-chain",
  },
  {
    icon: Megaphone,
    title: "Market Support & POSM",
    body: "Branded display material, local activation support and launch kits for new territories.",
    testid: "benefit-tile-market-support",
  },
  {
    icon: BadgePercent,
    title: "Attractive Profit Margins",
    body: "Trade pricing structured so distributors and retailers both win on every case.",
    testid: "benefit-tile-margins",
  },
  {
    icon: Store,
    title: "High-Volume Retail Demand",
    body: "The ₹5 price point and everyday hydration formats mean fast, predictable rotation.",
    testid: "benefit-tile-demand",
  },
];

const INITIAL = {
  name: "",
  business_name: "",
  location: "",
  distribution_type: "Wholesale",
  estimated_volume: "",
  phone: "",
  email: "",
  message: "",
};

export default function Partnership() {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitInquiry(form);
      toast.success("Inquiry sent. Our partnerships team will call you within 48 hours.");
      setForm(INITIAL);
    } catch {
      toast.error("Could not send your inquiry. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-white/10 bg-navy px-4 py-3.5 text-sm text-white placeholder:text-slate-500 focus:border-lime focus:outline-none transition-colors";

  return (
    <div className="pt-40">
      <section className="mx-auto max-w-6xl px-6">
        <Reveal>
          <Overline>Distribution Partnership</Overline>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
            Grow with a brand
            <span className="block text-lime">that moves.</span>
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-300">
            We are appointing distributors, super stockists and retail chains. Tell us about your
            business and our team will share territory availability and trade terms.
          </p>
        </Reveal>

        <Reveal delay={0.1} className="mt-14">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="partner-market-proof">
            {[
              ["5", "Cities surveyed before launch"],
              ["9", "Wholesalers already engaged"],
              ["11", "Hotels & restaurants on board"],
              ["₹5–₹50", "Pack sizes retailers asked for"],
            ].map(([v, l]) => (
              <div key={l} className="rounded-3xl border border-sun/25 bg-sun/5 p-6 text-center">
                <p className="font-display text-2xl sm:text-3xl font-extrabold text-sun-bright">{v}</p>
                <p className="mt-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{l}</p>
              </div>
            ))}
          </div>
        </Reveal>

        <div className="mt-20 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {BENEFITS.map((b, i) => (
            <Reveal key={b.title} delay={i * 0.1}>
              <div
                data-testid={b.testid}
                className="group h-full rounded-3xl border border-white/10 bg-navy-card p-7 transition-all duration-500 hover:border-lime/60 hover:-translate-y-2"
              >
                <div className="inline-flex rounded-2xl bg-lime/10 p-3.5 text-lime transition-colors duration-500 group-hover:bg-lime group-hover:text-navy">
                  <b.icon size={24} />
                </div>
                <h3 className="mt-6 font-display text-lg font-bold">{b.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-slate-400">{b.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <GrowthChart />

      <section className="mx-auto max-w-6xl px-6 pt-28">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.15fr] items-start">
          <Reveal>
            <div className="lg:sticky lg:top-32">
              <Overline>Inquiry Form</Overline>
              <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
                Tell us where you want to grow.
              </h2>
              <p className="mt-5 text-base leading-relaxed text-slate-400 max-w-md">
                Fill in the form and our partnerships team will reach out with pricing, territory
                details and onboarding steps — usually within 48 hours.
              </p>
              <div className="mt-9 flex flex-col gap-4 text-sm">
                <a data-testid="partner-phone" href={`tel:${CONTACT.phone}`} className="inline-flex items-center gap-3 text-slate-300 hover:text-sun-bright transition-colors">
                  <span className="rounded-full bg-sun/10 p-2.5 text-sun-bright"><Phone size={16} /></span>
                  +91&nbsp;{CONTACT.phone}
                </a>
                <a data-testid="partner-email" href={`mailto:${CONTACT.email}`} className="inline-flex items-center gap-3 text-slate-300 hover:text-sun-bright transition-colors">
                  <span className="rounded-full bg-sun/10 p-2.5 text-sun-bright"><Mail size={16} /></span>
                  {CONTACT.email}
                </a>
                <a data-testid="partner-email-alt" href={`mailto:${CONTACT.altEmail}`} className="inline-flex items-center gap-3 text-slate-300 hover:text-sun-bright transition-colors">
                  <span className="rounded-full bg-sun/10 p-2.5 text-sun-bright"><Mail size={16} /></span>
                  {CONTACT.altEmail}
                </a>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <form
              onSubmit={handleSubmit}
              data-testid="partner-inquiry-form"
              className="rounded-3xl border border-white/10 bg-navy-card p-7 sm:p-10 flex flex-col gap-4"
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <input data-testid="partner-form-name" required placeholder="Your name *" className={inputCls} value={form.name} onChange={set("name")} />
                <input data-testid="partner-form-business" required placeholder="Business name *" className={inputCls} value={form.business_name} onChange={set("business_name")} />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <input data-testid="partner-form-location" required placeholder="Location / territory *" className={inputCls} value={form.location} onChange={set("location")} />
                <select
                  data-testid="partner-form-type"
                  required
                  className={`${inputCls} appearance-none`}
                  value={form.distribution_type}
                  onChange={set("distribution_type")}
                >
                  <option>Wholesale</option>
                  <option>Super Stockist</option>
                  <option>Retail Chain</option>
                  <option>Distributor</option>
                  <option>Other</option>
                </select>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <input data-testid="partner-form-phone" required placeholder="Phone *" className={inputCls} value={form.phone} onChange={set("phone")} />
                <input data-testid="partner-form-email" required type="email" placeholder="Email *" className={inputCls} value={form.email} onChange={set("email")} />
              </div>
              <input data-testid="partner-form-volume" placeholder="Estimated monthly volume (optional)" className={inputCls} value={form.estimated_volume} onChange={set("estimated_volume")} />
              <textarea
                data-testid="partner-form-message"
                placeholder="Anything else we should know? (optional)"
                rows={4}
                className={`${inputCls} resize-none`}
                value={form.message}
                onChange={set("message")}
              />
              <button
                type="submit"
                data-testid="partner-form-submit"
                disabled={loading}
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-lime px-8 py-4 text-sm font-bold text-navy transition-colors hover:bg-sun-bright disabled:opacity-60"
              >
                {loading && <Loader2 size={16} className="animate-spin" />}
                {loading ? "Sending…" : "Submit Partnership Inquiry"}
              </button>
              <p className="text-xs text-slate-500 text-center">
                Your details go straight to our sales team. No spam, ever.
              </p>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
